import type { CodeableConcept } from "./common";
import { InvalidTransitionError, PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";
import type { PractitionerId } from "./practitioner.model";

/** Stable AllergyIntolerance identity, independent of the FHIR row locator. */
export type AllergyIntoleranceId = DomainId<"AllergyIntoleranceId">;

export function asAllergyIntoleranceId(value: string): AllergyIntoleranceId {
  if (!value) throw new PreconditionFailedError("An allergy identity cannot be empty");
  return value as AllergyIntoleranceId;
}

// ============================================================
// AllergyIntolerance — a substance this patient reacts to (ADR-0017)
//
// Two statuses that mean different things and are often confused: whether the
// allergy is still live (`clinicalStatus`) and whether we believe it at all
// (`verificationStatus`). Entered-in-error is the second kind — the record
// should never have existed — so it freezes everything, while `resolved` only
// closes the first.
// ============================================================

export const ALLERGY_INTOLERANCE_CATEGORIES = ["food", "medication", "environment", "biologic"] as const;
export type AllergyIntoleranceCategory = (typeof ALLERGY_INTOLERANCE_CATEGORIES)[number];

type ClinicalStatus = "active" | "inactive" | "resolved";
type VerificationStatus = "unconfirmed" | "confirmed" | "refuted" | "entered-in-error";

export const ALLERGY_REACTION_SEVERITIES = ["mild", "moderate", "severe"] as const;

interface Reaction {
  manifestation: CodeableConcept[];
  severity?: (typeof ALLERGY_REACTION_SEVERITIES)[number];
  description?: string;
}

export interface AllergyIntoleranceProps {
  id: AllergyIntoleranceId;
  /** The substance. Free text is allowed — "shellfish" is a real allergy. */
  code: CodeableConcept;
  patientId: PatientId;
  clinicalStatus?: ClinicalStatus;
  verificationStatus?: VerificationStatus;
  type?: "allergy" | "intolerance";
  category?: AllergyIntoleranceCategory[];
  criticality?: "low" | "high" | "unable-to-assess";
  recordedDate?: string;
  recorderId?: PractitionerId;
  reaction?: Reaction[];
}

export class AllergyIntolerance {
  private state: AllergyIntoleranceProps;

  private constructor(props: AllergyIntoleranceProps) {
    if (!props.patientId) {
      throw new PreconditionFailedError("Allergy requires a patient");
    }
    if (!props.code.coding?.length && !props.code.text) {
      throw new PreconditionFailedError("Allergy requires a substance");
    }
    this.state = structuredClone(props);
  }

  static create(input: AllergyIntoleranceProps): AllergyIntolerance {
    return new AllergyIntolerance({ ...structuredClone(input), id: asAllergyIntoleranceId(input.id) });
  }

  static restore(snapshot: AllergyIntoleranceProps): AllergyIntolerance {
    return new AllergyIntolerance(snapshot);
  }

  // ── facts ──

  get id(): AllergyIntoleranceId {
    return this.state.id;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  /** Still to be avoided — what a banner or a prescribing check asks. */
  get isLive(): boolean {
    return this.state.clinicalStatus !== "resolved" && !this.isRetracted;
  }

  /** Recorded in error. Not a clinical state — the record should not exist. */
  get isRetracted(): boolean {
    return this.state.verificationStatus === "entered-in-error";
  }

  snapshot(): AllergyIntoleranceProps {
    return structuredClone(this.state);
  }

  // ── operations ──

  /** The patient no longer reacts to it. */
  resolve(): void {
    this.assertMutable("resolve");
    if (this.state.clinicalStatus === "resolved") {
      throw new InvalidTransitionError(this.state.clinicalStatus, "AllergyIntolerance:resolve");
    }
    this.state.clinicalStatus = "resolved";
  }

  /** Revise what is known. Whether it is resolved is `resolve`'s business, not this one's. */
  revise(changes: {
    criticality?: AllergyIntoleranceProps["criticality"];
    type?: AllergyIntoleranceProps["type"];
    category?: AllergyIntoleranceProps["category"];
    verificationStatus?: VerificationStatus;
    clinicalStatus?: ClinicalStatus;
    reaction?: Reaction[];
  }): void {
    this.assertMutable("update");
    Object.assign(this.state, changes);
  }

  /** Record a reaction the patient had. */
  recordReaction(reaction: Reaction): void {
    this.assertMutable("record a reaction on");
    if (reaction.manifestation.length === 0) {
      throw new PreconditionFailedError("A reaction requires at least one manifestation");
    }
    this.state.reaction = [...(this.state.reaction ?? []), reaction];
  }

  // ── internals ──

  private assertMutable(action: string): void {
    if (this.isRetracted) {
      throw new PreconditionFailedError(`Cannot ${action} an allergy recorded in error`);
    }
  }
}
